import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Modal } from './Modal';

type Toast = { id: number; message: string; kind: 'success' | 'error' | 'info' };

type ToastCtx = {
  success: (m: string) => void;
  error: (m: string) => void;
  info: (m: string) => void;
};

const Ctx = createContext<ToastCtx>({ success: () => {}, error: () => {}, info: () => {} });

export function useToast() {
  return useContext(Ctx);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, kind: Toast['kind']) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, kind }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  }, []);

  const value = useMemo<ToastCtx>(
    () => ({
      success: (m) => push(m, 'success'),
      error: (m) => push(m, 'error'),
      info: (m) => push(m, 'info'),
    }),
    [push],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toast-stack">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`}>
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

type ConfirmOpts = {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
};

type ConfirmCtx = {
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
};

const ConfirmContext = createContext<ConfirmCtx>({ confirm: async () => false });

export function useConfirm() {
  return useContext(ConfirmContext);
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOpts & { resolve: (v: boolean) => void }) | null>(null);

  const confirm = useCallback((opts: ConfirmOpts) => {
    return new Promise<boolean>((resolve) => {
      setState({ ...opts, resolve });
    });
  }, []);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <Modal
        open={!!state}
        title={state?.title ?? ''}
        onClose={() => {
          state?.resolve(false);
          setState(null);
        }}
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                state?.resolve(false);
                setState(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`btn ${state?.danger ? 'btn-danger' : 'btn-primary'}`}
              onClick={() => {
                state?.resolve(true);
                setState(null);
              }}
            >
              {state?.confirmLabel ?? 'Confirm'}
            </button>
          </>
        }
      >
        <p>{state?.message}</p>
      </Modal>
    </ConfirmContext.Provider>
  );
}
